// src/pages/MentorDashboard.js
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { AuthContext } from '../context/AuthContext';
import { formatDate, getAssignmentStatus } from '../utils/assignmentUtils';
import '../styles/adminDashboard.css';
import '../styles/mentorDashboard.css';
import SubmissionViewer from '../components/common/SubmissionViewer';

const MentorDashboard = () => {
  const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [learners, setLearners] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [feedback, setFeedback] = useState({ positivePoints: '', improvementAreas: '' });
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [submissionsCache, setSubmissionsCache] = useState({});

  // Fetch mentor's group and learners
  useEffect(() => {
    const fetchMentorData = async () => {
      try {
        if (!currentUser?.uid) return;

        const mentorDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const mentorData = mentorDoc.data();

        if (!mentorData?.groupId) { setLoading(false); return; }

        const groupDoc = await getDoc(doc(db, 'groups', mentorData.groupId));
        const groupData = groupDoc.data();
        setGroupName(groupData?.name || 'Unnamed Group');

        // Query learners directly by groupId — single source of truth
        const usersInGroupSnapshot = await getDocs(
          query(collection(db, 'users'),
            where('groupId', '==', mentorData.groupId),
            where('role', '==', 'learner'))
        );
        const mergedLearnerIds = usersInGroupSnapshot.docs.map(d => d.id);

        if (!mergedLearnerIds.length) { setLoading(false); return; }

        const learnerPromises = mergedLearnerIds.map(async (learnerId) => {
          const learnerDoc = await getDoc(doc(db, 'users', learnerId));
          if (!learnerDoc.exists()) return null;
          return {
            id: learnerDoc.id,
            name: learnerDoc.data().name || 'Unnamed Learner',
            email: learnerDoc.data().email || '',
            pendingReviews: 0,
            updatedSubmissions: 0
          };
        });

        const learnersData = (await Promise.all(learnerPromises)).filter(Boolean);

        // Only count submissions for assignments that belong to this group
        const assignmentsSnapshot = await getDocs(collection(db, 'assignments'));
        const groupAssignmentIds = new Set();
        assignmentsSnapshot.forEach(d => {
          const data = d.data();
          if (data.groupSettings?.[mentorData.groupId]) groupAssignmentIds.add(d.id);
        });

        const allSubmissionsSnapshot = await getDocs(
          query(collection(db, 'submissions'), where('learnerId', 'in', learnersData.map(l => l.id)))
        );

        const submissionsByLearner = {};
        allSubmissionsSnapshot.forEach(docSnap => {
          const data = docSnap.data();
          // Skip submissions for assignments not in this group
          if (!groupAssignmentIds.has(data.assignmentId)) return;
          const lid = data.learnerId;
          if (!submissionsByLearner[lid]) submissionsByLearner[lid] = { pending: 0, updated: 0 };
          if (data.reviewStatus === 'pending' && data.readyForReview === true) submissionsByLearner[lid].pending++;
          if (data.submissionStatus === 'updated') submissionsByLearner[lid].updated++;
        });

        setLearners(learnersData.map(learner => ({
          ...learner,
          pendingReviews: submissionsByLearner[learner.id]?.pending || 0,
          updatedSubmissions: submissionsByLearner[learner.id]?.updated || 0
        })));
        setLoading(false);
      } catch (error) {
        console.error("Error fetching mentor data:", error);
        setLoading(false);
      }
    };

    fetchMentorData();
  }, [currentUser]);

  const fetchLearnerAssignments = useCallback(async (learnerId) => {
    try {
      setLoading(true);
      setSelectedAssignment(null);
      setSubmission(null);

      const learnerDoc = await getDoc(doc(db, 'users', learnerId));
      const groupId = learnerDoc.data().groupId;

      if (!groupId) { setAssignments([]); setLoading(false); return; }

      const [assignmentsSnapshot, submissionsSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'assignments'))),
        getDocs(query(collection(db, 'submissions'), where('learnerId', '==', learnerId)))
      ]);

      const submissionsByAssignment = {};
      submissionsSnapshot.forEach(docSnap => {
        const data = docSnap.data();
        submissionsByAssignment[data.assignmentId] = { id: docSnap.id, ...data };
      });

      const assignmentsData = [];
      assignmentsSnapshot.forEach(assignmentDoc => {
        const data = assignmentDoc.data();
        const id = assignmentDoc.id;

        const targetGroups = data.targetGroups || [];
        if (targetGroups.length > 0 && !targetGroups.includes(groupId)) return;

        const groupSettings = data.groupSettings?.[groupId];
        const startDate = formatDate(groupSettings?.startDate || data.startDate, 'date-object');
        const endDate = formatDate(groupSettings?.endDate || data.endDate, 'date-object');
        const assignmentStatus = groupSettings?.status || data.status || 'active';

        const submissionData = submissionsByAssignment[id];
        let submissionStatus = 'Not Started';
        let reviewStatus = null;

        if (submissionData) {
          if (submissionData.submissionStatus === 'draft') {
            submissionStatus = 'In Progress';
          } else if (submissionData.submissionStatus === 'submitted') {
            submissionStatus = submissionData.readyForReview ? 'Submitted' : 'In Progress';
          } else if (submissionData.submissionStatus === 'updated') {
            submissionStatus = 'Updated';
          }
          reviewStatus = submissionData.reviewStatus;
        }

        const assignmentObj = { id, title: data.title || 'Untitled', startDate, endDate, status: assignmentStatus };
        const { status: calculatedStatus, displayClass } = getAssignmentStatus(assignmentObj);

        assignmentsData.push({
          id,
          title: data.title || 'Untitled Assignment',
          startDate,
          endDate,
          assignmentStatus: calculatedStatus,
          statusClass: displayClass,
          submissionStatus,
          reviewStatus,
          submissionId: submissionData?.id
        });
      });

      assignmentsData.sort((a, b) => {
        if (!a.startDate) return 1;
        if (!b.startDate) return -1;
        return a.startDate - b.startDate;
      });

      setAssignments(assignmentsData);
      setSelectedLearner({ ...learnerDoc.data(), id: learnerId });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching learner assignments:", error);
      setLoading(false);
    }
  }, []);

  const fetchSubmissionDetails = useCallback(async (assignmentId, submissionId, learnerId) => {
    try {
      setLoading(true);

      const cacheKey = `${assignmentId}-${learnerId}`;
      if (submissionsCache[cacheKey] && Date.now() - submissionsCache[cacheKey].timestamp < 300000) {
        const cached = submissionsCache[cacheKey];
        setSelectedAssignment(cached.assignment);
        setSubmission(cached.submission);
        setFeedback({
          positivePoints: cached.submission?.feedback?.positivePoints || '',
          improvementAreas: cached.submission?.feedback?.improvementAreas || ''
        });
        setLoading(false);
        return;
      }

      const assignmentDoc = await getDoc(doc(db, 'assignments', assignmentId));
      if (!assignmentDoc.exists()) { setLoading(false); return; }

      const assignmentData = {
        id: assignmentDoc.id,
        ...assignmentDoc.data(),
        startDate: assignmentDoc.data().startDate?.toDate() || null,
        endDate: assignmentDoc.data().endDate?.toDate() || null
      };

      const promises = [
        submissionId
          ? getDoc(doc(db, 'submissions', submissionId))
          : getDocs(query(collection(db, 'submissions'), where('assignmentId', '==', assignmentId), where('learnerId', '==', learnerId))),
        getDocs(query(collection(db, 'diaryEntries'), where('assignmentId', '==', assignmentId), where('learnerId', '==', learnerId)))
      ];

      const results = await Promise.all(promises);

      let submissionData = null;
      if (submissionId && results[0].exists()) {
        submissionData = {
          id: results[0].id,
          ...results[0].data(),
          createdAt: results[0].data().createdAt?.toDate() || null,
          updatedAt: results[0].data().updatedAt?.toDate() || null
        };
      } else if (!submissionId && results[0].size > 0) {
        const d = results[0].docs[0];
        submissionData = {
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate() || null,
          updatedAt: d.data().updatedAt?.toDate() || null
        };
      }

      let diaryData = null;
      if (results[1].size > 0) {
        const d = results[1].docs[0];
        diaryData = { id: d.id, ...d.data() };
      }

      let feedbackData = null;
      if (submissionData?.mentorFeedback) {
        feedbackData = {
          id: submissionData.feedbackId || 'embedded',
          ...submissionData.mentorFeedback,
          positivePoints: submissionData.mentorFeedback.positivePoints || '',
          improvementAreas: submissionData.mentorFeedback.improvementAreas || ''
        };
      }

      const fullSubmission = { ...submissionData, feedback: feedbackData, diary: diaryData };

      setSubmissionsCache(prev => ({
        ...prev,
        [cacheKey]: { assignment: assignmentData, submission: fullSubmission, timestamp: Date.now() }
      }));

      setSelectedAssignment(assignmentData);
      setSubmission(fullSubmission);
      setFeedback({
        positivePoints: feedbackData?.positivePoints || '',
        improvementAreas: feedbackData?.improvementAreas || ''
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching submission details:", error);
      setLoading(false);
    }
  }, [submissionsCache]);

  const submitFeedback = useCallback(async () => {
    if (!selectedAssignment || !submission || !currentUser?.uid) return;

    try {
      setFeedbackSubmitting(true);

      const feedbackData = {
        content: feedback.positivePoints + '\n\n' + feedback.improvementAreas,
        positivePoints: feedback.positivePoints,
        improvementAreas: feedback.improvementAreas,
        submissionId: submission.id,
        learnerId: submission.learnerId,
        mentorId: currentUser.uid,
        updatedAt: serverTimestamp()
      };

      let feedbackId;
      if (submission.feedback?.id && submission.feedback.id !== 'embedded') {
        feedbackId = submission.feedback.id;
        await updateDoc(doc(db, 'feedback', feedbackId), feedbackData);
      } else {
        feedbackData.createdAt = serverTimestamp();
        const feedbackRef = await addDoc(collection(db, 'feedback'), feedbackData);
        feedbackId = feedbackRef.id;
      }

      await updateDoc(doc(db, 'submissions', submission.id), {
        reviewStatus: 'reviewed',
        feedbackId,
        mentorFeedback: {
          positivePoints: feedback.positivePoints,
          improvementAreas: feedback.improvementAreas,
          updatedAt: serverTimestamp()
        },
        updatedAt: serverTimestamp()
      });

      setSubmissionsCache({});
      await fetchSubmissionDetails(selectedAssignment.id, submission.id, submission.learnerId);
      await fetchLearnerAssignments(submission.learnerId);
      setFeedbackSubmitting(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setFeedbackSubmitting(false);
    }
  }, [selectedAssignment, submission, feedback, currentUser, fetchSubmissionDetails, fetchLearnerAssignments]);

  const handleLearnerSelect = useCallback((learnerId) => {
    fetchLearnerAssignments(learnerId);
  }, [fetchLearnerAssignments]);

  const handleAssignmentSelect = useCallback((assignment) => {
    fetchSubmissionDetails(assignment.id, assignment.submissionId || null, selectedLearner.id);
  }, [fetchSubmissionDetails, selectedLearner]);

  const handleBackToAssignments = useCallback(() => {
    setSelectedAssignment(null);
    setSubmission(null);
  }, []);

  const learnersList = useMemo(() => (
    <ul className="group-items">
      {learners.map((learner) => (
        <li
          key={learner.id}
          className={`group-item ${selectedLearner?.id === learner.id ? 'selected' : ''}`}
          onClick={() => handleLearnerSelect(learner.id)}
        >
          <div className="group-name">{learner.name}</div>
          <div className="group-size">
            <div className="learner-email">{learner.email}</div>
          </div>
          {(learner.pendingReviews > 0 || learner.updatedSubmissions > 0) && (
            <div className="review-badge">
              {learner.pendingReviews > 0 && (
                <span className="status-badge status-pending">{learner.pendingReviews} Pending</span>
              )}
              {learner.updatedSubmissions > 0 && (
                <span className="status-badge status-pending" style={{ marginLeft: '5px' }}>
                  {learner.updatedSubmissions} Updated
                </span>
              )}
            </div>
          )}
        </li>
      ))}
    </ul>
  ), [learners, selectedLearner, handleLearnerSelect]);

  const assignmentsTable = useMemo(() => (
    <table className="data-table">
      <thead>
        <tr>
          <th>Assignment</th>
          <th>Assignment Status</th>
          <th>Submission Status</th>
          <th>Dates</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        {assignments.map((assignment) => (
          <tr key={assignment.id}>
            <td>{assignment.title}</td>
            <td>
              <span className={`status-badge ${assignment.statusClass}`}>
                {assignment.assignmentStatus}
              </span>
            </td>
            <td>
              <span className={`status-badge status-${assignment.submissionStatus.toLowerCase().replace(' ', '-')}`}>
                {assignment.submissionStatus}
              </span>
            </td>
            <td>
              {assignment.startDate
                ? <div>{assignment.startDate.toLocaleDateString()} – {assignment.endDate ? assignment.endDate.toLocaleDateString() : 'Ongoing'}</div>
                : <div className="no-dates-warning">Check assignment dates</div>}
            </td>
            <td>
              <button
                className={`btn-small ${
                  assignment.submissionStatus === 'Submitted' ? 'btn-primary' :
                  assignment.submissionStatus === 'Updated' ? 'btn-warning' : 'btn-secondary'
                }`}
                onClick={() => handleAssignmentSelect(assignment)}
              >
                {assignment.submissionStatus === 'Submitted' ? 'Review' :
                 assignment.submissionStatus === 'Updated' ? 'Review Update' :
                 assignment.reviewStatus === 'reviewed' ? 'View Feedback' : 'View'}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  ), [assignments, handleAssignmentSelect]);

  return (
    <div className="admin-dashboard">
      <h1>Mentor Dashboard</h1>
      {groupName && <h2>Group: {groupName}</h2>}

      {loading ? (
        <div className="loading">Loading data...</div>
      ) : (
        <div className="dashboard-container">
          {selectedAssignment && submission ? (
            <div className="submission-review">
              <div className="form-header">
                <h3>{selectedAssignment.title}</h3>
                <div className="form-actions">
                  <button className="btn-secondary" onClick={handleBackToAssignments}>
                    Back to Assignments
                  </button>
                </div>
              </div>

              <div className="form-body">
                <div className="form-group">
                  <label>Assignment Instructions</label>
                  <div className="form-value instruction-text">
                    {selectedAssignment.instruction || 'No instructions provided'}
                  </div>
                </div>

                <div className="form-group">
                  <label>Learner Submission</label>
                  {submission ? (
                    <div>
                      {submission.content && (
                        <div className="form-value">{submission.content}</div>
                      )}
                      {submission.fileUrls?.length > 0 && (
                        <div className="submission-files">
                          <h4>Uploaded Files</h4>
                          <ul>
                            {submission.fileUrls.map((file, i) => (
                              <li key={i}>
                                <a href={file.url} target="_blank" rel="noopener noreferrer">{file.name}</a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="submission-content">
                        <SubmissionViewer submission={submission} />
                      </div>
                      <div className="submission-meta">
                        <p>Submitted: {submission.createdAt?.toLocaleDateString()} {submission.createdAt?.toLocaleTimeString()}</p>
                        {submission.updatedAt && (
                          <p>Last Updated: {submission.updatedAt?.toLocaleDateString()} {submission.updatedAt?.toLocaleTimeString()}</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="no-submission">No submission found for this assignment</div>
                  )}
                </div>

                {submission?.diary && (
                  <div className="form-group">
                    <label>Learner Reflection Diary</label>
                    <div className="diary-entries">
                      <div className="form-group">
                        <label>What new skills or knowledge did I gain in this assignment?</label>
                        <div className="form-value">{submission.diary.skills || 'Not provided'}</div>
                      </div>
                      <div className="form-group">
                        <label>How did this topic make me feel?</label>
                        <div className="form-value">{submission.diary.feelings || 'Not provided'}</div>
                      </div>
                    </div>
                  </div>
                )}

                {submission && (
                  <div className="feedback-section">
                    <h3>Provide Feedback</h3>

                    {submission.reviewStatus === 'reviewed' && submission.mentorFeedback && (
                      <div className="current-feedback-notice">
                        <p>This submission has been reviewed. You can update your feedback below.</p>
                        <div className="form-value">
                          <strong>Last updated:</strong> {submission.mentorFeedback.updatedAt?.toDate?.().toLocaleString() || 'Unknown date'}
                        </div>
                      </div>
                    )}

                    <div className="form-group">
                      <label>What was good about this submission?</label>
                      <textarea
                        className="form-control"
                        value={feedback.positivePoints}
                        onChange={(e) => setFeedback({ ...feedback, positivePoints: e.target.value })}
                        placeholder="Highlight the strengths of this submission"
                      />
                    </div>

                    <div className="form-group">
                      <label>Areas for improvement</label>
                      <textarea
                        className="form-control"
                        value={feedback.improvementAreas}
                        onChange={(e) => setFeedback({ ...feedback, improvementAreas: e.target.value })}
                        placeholder="Suggest ways the learner could improve their work"
                      />
                    </div>

                    <div className="form-actions">
                      <button
                        className="btn-primary"
                        onClick={submitFeedback}
                        disabled={feedbackSubmitting || !feedback.positivePoints.trim()}
                      >
                        {feedbackSubmitting ? 'Submitting...' : submission.feedback ? 'Update Feedback' : 'Submit Feedback'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="groups-container">
              <div className="groups-list">
                <div className="list-header">
                  <h3>Your Learners</h3>
                </div>
                {learners.length > 0 ? learnersList : (
                  <div className="empty-list">No learners assigned to you</div>
                )}
              </div>

              <div className="assignment-details">
                {selectedLearner ? (
                  <div>
                    <div className="form-header">
                      <h3>Assignments for {selectedLearner.name}</h3>
                    </div>
                    {assignments.length > 0 ? assignmentsTable : (
                      <div className="empty-list">No assignments found for this learner</div>
                    )}
                  </div>
                ) : (
                  <div className="no-selection">Select a learner to view their assignments</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MentorDashboard;